/**
 * MechanicProductivityAutoCard
 * -----------------------------------------------------------------------------
 * Surfaces the auto-derived productivity rollup from /api/super-admin/
 * mechanic-productivity/auto-rollup.
 *
 * iter102 (Mar 2026):
 *   • Own preset filter — Current month / Previous month / Last 30 days /
 *     Last 90 days / This year / Custom range. Decouples this card from
 *     FinancialsTab's month-year so SA can pull a different period here
 *     without losing context elsewhere.
 *   • Workshop-level Print + CSV export.
 *   • Per-mechanic Print + CSV export — one click each.
 *
 * Sits ABOVE the manual hours grid so SA can see the clock-log truth and
 * still override anything in the manual table.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowUp, ArrowDown, CheckCircle2, Activity, RefreshCw, AlertCircle,
  DollarSign, Printer, FileDown, Calendar as CalendarIcon, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDollarSigned = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (n > 0) return `+$${formatted}`;
  if (n < 0) return `−$${formatted}`;
  return '$0';
};

// Convert a Date → ISO YYYY-MM-DD (local).
const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Convert a preset key → { start, end, label }.
const presetRange = (key) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (key === 'current_month') {
    const s = new Date(y, m, 1);
    return { start: toIso(s), end: toIso(today), label: `Current month` };
  }
  if (key === 'previous_month') {
    const s = new Date(y, m - 1, 1);
    const e = new Date(y, m, 0);
    return { start: toIso(s), end: toIso(e), label: `Previous month` };
  }
  if (key === 'last_30') {
    const s = new Date(today); s.setDate(s.getDate() - 29);
    return { start: toIso(s), end: toIso(today), label: 'Last 30 days' };
  }
  if (key === 'last_90') {
    const s = new Date(today); s.setDate(s.getDate() - 89);
    return { start: toIso(s), end: toIso(today), label: 'Last 90 days' };
  }
  if (key === 'this_year') {
    const s = new Date(y, 0, 1);
    return { start: toIso(s), end: toIso(today), label: 'This year' };
  }
  // custom — caller manages start/end manually.
  return null;
};

function buildWorkshopCsv(rows, totals, period) {
  const head = [
    'Mechanic', 'Actual h', 'Quoted h', 'Variance h', '$ Impact (AUD)',
    'Gain jobs', 'Loss jobs', 'On-track', 'In progress',
  ];
  const lines = [head.join(',')];
  rows.forEach((m) => {
    lines.push([
      `"${(m.mechanic_name || '').replace(/"/g, '""')}"`,
      (m.actual_hours || 0).toFixed(2),
      (m.quoted_hours || 0).toFixed(2),
      (m.variance_hours || 0).toFixed(2),
      (m.dollar_impact_aud || 0).toFixed(2),
      m.gain_jobs || 0, m.loss_jobs || 0, m.on_track_jobs || 0, m.in_progress_jobs || 0,
    ].join(','));
  });
  lines.push('');
  lines.push(`Totals,,,,`);
  lines.push(`Actual hours,${(totals.actual_hours || 0).toFixed(2)}`);
  lines.push(`Quoted hours,${(totals.quoted_hours || 0).toFixed(2)}`);
  lines.push(`$ Impact (AUD),${(totals.dollar_impact_aud || 0).toFixed(2)}`);
  lines.push(`Gain jobs,${totals.gain_jobs || 0}`);
  lines.push(`Loss jobs,${totals.loss_jobs || 0}`);
  lines.push(`On-track jobs,${totals.on_track_jobs || 0}`);
  lines.push('');
  lines.push(`Period,${period}`);
  return lines.join('\r\n');
}

function buildMechanicCsv(m, period, labourRate) {
  const lines = [`Mechanic productivity — ${m.mechanic_name}`];
  lines.push(`Period,${period}`);
  lines.push(`Labour rate (AUD/hr),${labourRate}`);
  lines.push('');
  lines.push(['Metric', 'Value'].join(','));
  lines.push(`Total actual hours,${(m.actual_hours || 0).toFixed(2)}`);
  lines.push(`Total quoted hours,${(m.quoted_hours || 0).toFixed(2)}`);
  lines.push(`Variance hours,${(m.variance_hours || 0).toFixed(2)}`);
  lines.push(`Closed-jobs actual hours,${(m.closed_actual_hours || 0).toFixed(2)}`);
  lines.push(`Closed-jobs quoted hours,${(m.closed_quoted_hours || 0).toFixed(2)}`);
  lines.push(`Closed-jobs variance hours,${(m.closed_variance_hours || 0).toFixed(2)}`);
  lines.push(`Dollar impact (AUD),${(m.dollar_impact_aud || 0).toFixed(2)}`);
  lines.push(`Gain jobs,${m.gain_jobs || 0}`);
  lines.push(`Loss jobs,${m.loss_jobs || 0}`);
  lines.push(`On-track jobs,${m.on_track_jobs || 0}`);
  lines.push(`In-progress jobs,${m.in_progress_jobs || 0}`);
  return lines.join('\r\n');
}

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

function escape(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function printWorkshop(rows, totals, period, labourRate, workshopName = 'Workshop') {
  const w = window.open('', '_blank');
  if (!w) return;
  const tbody = rows.map((m, i) => `
    <tr>
      <td>${i + 1}</td>
      <td><strong>${escape(m.mechanic_name)}</strong></td>
      <td style="text-align:right;">${(m.actual_hours || 0).toFixed(2)}h</td>
      <td style="text-align:right;color:#475569;">${(m.quoted_hours || 0).toFixed(2)}h</td>
      <td style="text-align:right;color:${(m.variance_hours || 0) > 0 ? '#dc2626' : '#059669'};font-weight:600;">
        ${(m.variance_hours || 0) > 0 ? '+' : ''}${(m.variance_hours || 0).toFixed(2)}h
      </td>
      <td style="text-align:right;color:${(m.dollar_impact_aud || 0) > 0 ? '#059669' : (m.dollar_impact_aud || 0) < 0 ? '#dc2626' : '#475569'};font-weight:700;">
        ${fmtDollarSigned(m.dollar_impact_aud || 0)}
      </td>
      <td style="text-align:right;">${m.gain_jobs || 0} / ${m.loss_jobs || 0} / ${m.on_track_jobs || 0}</td>
      <td style="text-align:right;color:#475569;">${m.in_progress_jobs || 0}</td>
    </tr>
  `).join('');

  w.document.write(`
    <!DOCTYPE html><html><head><meta charset="utf-8">
    <title>Workshop Productivity — ${escape(period)}</title>
    <style>
      * { font-family: -apple-system, system-ui, sans-serif; }
      body { max-width: 1000px; margin: 24px auto; padding: 0 24px; color: #1e293b; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      .meta { color: #64748b; font-size: 12px; margin-bottom: 16px; }
      .stat-row { display:flex; gap:12px; margin-bottom:24px; flex-wrap:wrap; }
      .stat { border:1px solid #cbd5e1; border-radius:8px; padding:8px 14px; }
      .stat .k { font-size:11px; color:#64748b; text-transform:uppercase; letter-spacing:0.05em; font-weight:600; }
      .stat .v { font-size:20px; font-weight:700; color:#0f172a; margin-top:4px; }
      table { width:100%; border-collapse: collapse; font-size: 12px; }
      th, td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
      th { background:#f8fafc; color:#334155; font-size:11px; text-transform:uppercase; letter-spacing:0.05em; text-align:left; }
      thead th:not(:first-child):not(:nth-child(2)) { text-align:right; }
      .footer { margin-top:32px; color:#64748b; font-size:11px; text-align:center; }
      @media print { body { margin:0; padding:12px; } .no-print { display:none; } }
    </style>
    </head><body>
      <h1>${escape(workshopName)} — Workshop Productivity Report</h1>
      <div class="meta">Period: ${escape(period)} · Labour rate: $${labourRate.toFixed(0)}/hr · Generated ${new Date().toLocaleString('en-AU')}</div>
      <div class="stat-row">
        <div class="stat"><div class="k">Total actual</div><div class="v">${(totals.actual_hours || 0).toFixed(1)}h</div></div>
        <div class="stat"><div class="k">Total quoted</div><div class="v">${(totals.quoted_hours || 0).toFixed(1)}h</div></div>
        <div class="stat"><div class="k">Gain / Loss / On-track</div><div class="v">${totals.gain_jobs || 0} / ${totals.loss_jobs || 0} / ${totals.on_track_jobs || 0}</div></div>
        <div class="stat"><div class="k">Net $ impact</div><div class="v" style="color:${(totals.dollar_impact_aud || 0) > 0 ? '#059669' : (totals.dollar_impact_aud || 0) < 0 ? '#dc2626' : '#0f172a'};">${fmtDollarSigned(totals.dollar_impact_aud || 0)}</div></div>
      </div>
      <table><thead><tr>
        <th>#</th><th>Mechanic</th><th>Actual</th><th>Quoted</th><th>Variance</th>
        <th>$ Impact</th><th>Gain / Loss / On-track</th><th>In progress</th>
      </tr></thead><tbody>${tbody}</tbody></table>
      <div class="footer">AutoShopFlow · Workshop Productivity</div>
      <div class="no-print" style="text-align:center;margin-top:24px;">
        <button onclick="window.print()" style="padding:8px 24px;background:#0f172a;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;">Print</button>
      </div>
    </body></html>
  `);
  w.document.close();
  setTimeout(() => w.print(), 500);
}

function printMechanic(m, period, labourRate, workshopName = 'Workshop') {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`
    <!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${escape(m.mechanic_name)} — Productivity</title>
    <style>
      * { font-family: -apple-system, system-ui, sans-serif; }
      body { max-width: 700px; margin: 24px auto; padding: 0 24px; color: #1e293b; }
      h1 { font-size: 20px; margin-bottom: 4px; }
      h2 { font-size: 16px; color: #475569; margin-top: 24px; margin-bottom: 8px; }
      .meta { color: #64748b; font-size: 12px; margin-bottom: 16px; }
      table { width:100%; border-collapse: collapse; font-size: 13px; }
      td { padding: 6px 10px; border-bottom: 1px solid #e2e8f0; }
      td.k { color:#64748b; }
      td.v { text-align:right; font-weight:600; }
      .footer { margin-top:32px; color:#64748b; font-size:11px; text-align:center; }
      @media print { body { margin:0; padding:12px; } .no-print { display:none; } }
    </style>
    </head><body>
      <h1>${escape(workshopName)} — Productivity Report</h1>
      <div class="meta">Mechanic: <strong>${escape(m.mechanic_name)}</strong> · Period: ${escape(period)} · Labour rate: $${labourRate.toFixed(0)}/hr</div>
      <h2>Total period</h2>
      <table>
        <tr><td class="k">Total actual hours</td><td class="v">${(m.actual_hours || 0).toFixed(2)}h</td></tr>
        <tr><td class="k">Total quoted hours</td><td class="v">${(m.quoted_hours || 0).toFixed(2)}h</td></tr>
        <tr><td class="k">Variance hours</td><td class="v" style="color:${(m.variance_hours || 0) > 0 ? '#dc2626' : '#059669'};">
          ${(m.variance_hours || 0) > 0 ? '+' : ''}${(m.variance_hours || 0).toFixed(2)}h
        </td></tr>
      </table>
      <h2>Closed jobs only</h2>
      <table>
        <tr><td class="k">Actual on closed jobs</td><td class="v">${(m.closed_actual_hours || 0).toFixed(2)}h</td></tr>
        <tr><td class="k">Quoted on closed jobs</td><td class="v">${(m.closed_quoted_hours || 0).toFixed(2)}h</td></tr>
        <tr><td class="k">$ Impact (AUD)</td><td class="v" style="color:${(m.dollar_impact_aud || 0) > 0 ? '#059669' : (m.dollar_impact_aud || 0) < 0 ? '#dc2626' : '#0f172a'};">
          ${fmtDollarSigned(m.dollar_impact_aud || 0)}
        </td></tr>
      </table>
      <h2>Job-card outcome counts</h2>
      <table>
        <tr><td class="k">Gain jobs (faster than quoted)</td><td class="v" style="color:#059669;">${m.gain_jobs || 0}</td></tr>
        <tr><td class="k">Loss jobs (slower than quoted)</td><td class="v" style="color:#dc2626;">${m.loss_jobs || 0}</td></tr>
        <tr><td class="k">On-track jobs</td><td class="v">${m.on_track_jobs || 0}</td></tr>
        <tr><td class="k">Still in progress</td><td class="v" style="color:#475569;">${m.in_progress_jobs || 0}</td></tr>
      </table>
      <div class="footer">Generated ${new Date().toLocaleString('en-AU')}</div>
      <div class="no-print" style="text-align:center;margin-top:24px;">
        <button onclick="window.print()" style="padding:8px 24px;background:#0f172a;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;">Print</button>
      </div>
    </body></html>
  `);
  w.document.close();
  setTimeout(() => w.print(), 500);
}

export default function MechanicProductivityAutoCard({ month, year, MONTH_NAMES = [], workshopName = 'Workshop' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Own date filter so SA can scope this card independently of the
  // FinancialsTab month/year selector. Defaults to "current month".
  const [preset, setPreset] = useState('current_month');
  const initial = useMemo(() => presetRange('current_month'), []);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);

  const periodLabel = useMemo(() => {
    if (preset === 'custom') return `${start} → ${end}`;
    return presetRange(preset)?.label || `${start} → ${end}`;
  }, [preset, start, end]);

  // Whenever the preset changes (other than custom), rebuild start/end.
  useEffect(() => {
    if (preset === 'custom') return;
    const r = presetRange(preset);
    if (r) { setStart(r.start); setEnd(r.end); }
  }, [preset]);

  // If the parent passed month/year and the user has NOT customised the
  // filter, keep the card in sync with FinancialsTab's selector for
  // backward-compat. Heuristic: month/year drift from preset start.
  useEffect(() => {
    if (preset !== 'current_month') return;
    // Only run if month+year are valid.
    if (!month || !year) return;
    const today = new Date();
    if (month === today.getMonth() + 1 && year === today.getFullYear()) return;
    // Parent moved off "current month" — switch to the parent's month.
    const last = new Date(year, month, 0);
    const first = new Date(year, month - 1, 1);
    setPreset('custom');
    setStart(toIso(first));
    setEnd(toIso(last));
  }, [month, year, preset]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/super-admin/mechanic-productivity/auto-rollup?start_date=${start}&end_date=${end}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, [start, end]);

  const mechanics = (data?.mechanics || []).filter(m => m.mechanic_id !== 'unassigned');
  const totals = data?.totals || {};
  const labourRate = data?.labour_rate || 0;
  const totalImpact = totals.dollar_impact_aud || 0;

  const periodFile = periodLabel.replace(/[^A-Za-z0-9]/g, '_');

  return (
    <Card className="p-6" data-testid="mechanic-productivity-auto-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Auto-derived from Clock Log
            <Badge variant="outline" className="text-[10px] font-normal ml-1">live</Badge>
          </h3>
          <p className="text-sm text-slate-500">
            Computed from mechanic clock-in / clock-out · <strong>{periodLabel}</strong>
            {labourRate ? ` · labour rate $${labourRate.toFixed(0)}/h` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`hidden md:flex flex-col items-end px-3 py-1.5 rounded-lg border ${
              totalImpact > 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-800' :
              totalImpact < 0 ? 'border-rose-300 bg-rose-50 text-rose-800' :
              'border-slate-200 bg-slate-50 text-slate-700'
            }`}
            data-testid="auto-rollup-total-impact"
            title="Sum of (quoted − actual) hours × labour rate across all closed job cards in this period"
          >
            <span className="text-[10px] uppercase tracking-wide font-semibold opacity-70">
              {totalImpact >= 0 ? 'Workshop gain' : 'Workshop loss'}
            </span>
            <span className="text-base font-bold leading-none flex items-center gap-0.5">
              <DollarSign className="h-3.5 w-3.5" />
              {fmtDollarSigned(totalImpact).replace(/^[+−]?\$?/, '')}
            </span>
          </div>
        </div>
      </div>

      {/* Filter row — preset + optional custom range + refresh + workshop actions */}
      <div className="flex items-end gap-2 flex-wrap mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 mb-1">
            <CalendarIcon className="h-3 w-3" /> Period
          </label>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="h-9 w-44" data-testid="prod-auto-preset-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current_month">Current month</SelectItem>
              <SelectItem value="previous_month">Previous month</SelectItem>
              <SelectItem value="last_30">Last 30 days</SelectItem>
              <SelectItem value="last_90">Last 90 days</SelectItem>
              <SelectItem value="this_year">This year</SelectItem>
              <SelectItem value="custom">Custom range…</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {preset === 'custom' && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">From</label>
              <Input
                type="date" value={start} onChange={(e) => setStart(e.target.value)}
                className="h-9 w-40" data-testid="prod-auto-start"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">To</label>
              <Input
                type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                className="h-9 w-40" data-testid="prod-auto-end"
              />
            </div>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="auto-rollup-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline" size="sm"
            disabled={!data || mechanics.length === 0}
            onClick={() => printWorkshop(mechanics, totals, periodLabel, labourRate, workshopName)}
            data-testid="prod-auto-print-workshop"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Print Workshop
          </Button>
          <Button
            variant="outline" size="sm"
            disabled={!data || mechanics.length === 0}
            onClick={() => downloadCsv(
              buildWorkshopCsv(mechanics, totals, periodLabel),
              `workshop_productivity_${periodFile}.csv`,
            )}
            data-testid="prod-auto-csv-workshop"
          >
            <FileDown className="h-3.5 w-3.5 mr-1.5" />
            CSV Workshop
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 p-2 rounded mb-3">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Period totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4 text-xs">
        <div className="border border-slate-200 rounded p-2">
          <p className="text-slate-500">Actual hours</p>
          <p className="text-lg font-semibold text-slate-900">{(totals.actual_hours || 0).toFixed(1)}h</p>
        </div>
        <div className="border border-slate-200 rounded p-2">
          <p className="text-slate-500">Quoted hours</p>
          <p className="text-lg font-semibold text-slate-900">{(totals.quoted_hours || 0).toFixed(1)}h</p>
        </div>
        <div className="border border-emerald-200 bg-emerald-50/40 rounded p-2">
          <p className="text-emerald-700 flex items-center gap-1"><ArrowUp className="h-3 w-3" />Gain jobs</p>
          <p className="text-lg font-semibold text-emerald-700">{totals.gain_jobs || 0}</p>
        </div>
        <div className="border border-rose-200 bg-rose-50/40 rounded p-2">
          <p className="text-rose-700 flex items-center gap-1"><ArrowDown className="h-3 w-3" />Loss jobs</p>
          <p className="text-lg font-semibold text-rose-700">{totals.loss_jobs || 0}</p>
        </div>
        <div className="border border-slate-200 rounded p-2">
          <p className="text-slate-500 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />On track</p>
          <p className="text-lg font-semibold text-slate-700">{totals.on_track_jobs || 0}</p>
        </div>
        <div className={`border rounded p-2 ${
          totalImpact > 0 ? 'border-emerald-200 bg-emerald-50/40' :
          totalImpact < 0 ? 'border-rose-200 bg-rose-50/40' : 'border-slate-200'
        }`}>
          <p className={`flex items-center gap-1 ${
            totalImpact > 0 ? 'text-emerald-700' : totalImpact < 0 ? 'text-rose-700' : 'text-slate-500'
          }`}><DollarSign className="h-3 w-3" />Net $ impact</p>
          <p className={`text-lg font-semibold ${
            totalImpact > 0 ? 'text-emerald-700' : totalImpact < 0 ? 'text-rose-700' : 'text-slate-700'
          }`}>{fmtDollarSigned(totalImpact)}</p>
        </div>
      </div>

      {/* Per-mechanic table */}
      {mechanics.length === 0 && !loading ? (
        <p className="text-sm text-slate-500 py-4 text-center" data-testid="auto-rollup-empty">
          No clock-log data yet for this period. Once mechanics start work, their hours appear here automatically.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Mechanic</th>
                <th className="py-2 px-3 text-right">Actual</th>
                <th className="py-2 px-3 text-right">Quoted</th>
                <th className="py-2 px-3 text-right">Variance</th>
                <th className="py-2 px-3 text-right">$ Impact</th>
                <th className="py-2 px-3 text-right">Gain / Loss / On-track</th>
                <th className="py-2 px-3 text-right">In progress</th>
                <th className="py-2 px-3 text-right">Report</th>
              </tr>
            </thead>
            <tbody>
              {mechanics.map((m) => {
                const variance = m.variance_hours || 0;
                const dollarImpact = m.dollar_impact_aud || 0;
                const tone =
                  variance < 0 ? 'text-emerald-700 bg-emerald-50' :
                  variance > 0 ? 'text-rose-700 bg-rose-50' :
                  'text-slate-700 bg-slate-50';
                const dollarTone =
                  dollarImpact > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                  dollarImpact < 0 ? 'text-rose-700 bg-rose-50 border-rose-200' :
                  'text-slate-700 bg-slate-50 border-slate-200';
                const safeFile = (m.mechanic_name || 'mechanic').replace(/[^A-Za-z0-9]/g, '_');
                return (
                  <tr
                    key={m.mechanic_id}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                    data-testid={`auto-rollup-row-${m.mechanic_id}`}
                  >
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-slate-800 flex items-center gap-2 flex-wrap">
                        <span>{m.mechanic_name}</span>
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${dollarTone}`}
                          title={`Closed jobs MTD — quoted ${(m.closed_quoted_hours || 0).toFixed(1)}h vs actual ${(m.closed_actual_hours || 0).toFixed(1)}h @ $${labourRate.toFixed(0)}/hr`}
                          data-testid={`auto-rollup-dollar-pill-${m.mechanic_id}`}
                        >
                          <DollarSign className="h-2.5 w-2.5" />
                          {fmtDollarSigned(dollarImpact).replace(/^[+−]?\$?/, '')}
                          <span className="opacity-70 ml-0.5">
                            {dollarImpact > 0 ? 'gain' : dollarImpact < 0 ? 'loss' : 'flat'}
                          </span>
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        SM quoted {(m.closed_quoted_hours || 0).toFixed(1)}h · clocked {(m.closed_actual_hours || 0).toFixed(1)}h on closed JCs
                      </p>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono">{(m.actual_hours || 0).toFixed(2)}h</td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">{(m.quoted_hours || 0).toFixed(2)}h</td>
                    <td className={`py-2.5 px-3 text-right font-mono font-semibold rounded ${tone}`}>
                      {variance > 0 ? '+' : ''}{variance.toFixed(2)}h
                    </td>
                    <td className={`py-2.5 px-3 text-right font-mono font-semibold rounded ${
                      dollarImpact > 0 ? 'text-emerald-700 bg-emerald-50' :
                      dollarImpact < 0 ? 'text-rose-700 bg-rose-50' :
                      'text-slate-700 bg-slate-50'
                    }`}>
                      {fmtDollarSigned(dollarImpact)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs">
                      <span className="text-emerald-700 font-semibold">{m.gain_jobs}</span>
                      <span className="text-slate-300 mx-1">·</span>
                      <span className="text-rose-700 font-semibold">{m.loss_jobs}</span>
                      <span className="text-slate-300 mx-1">·</span>
                      <span className="text-slate-700">{m.on_track_jobs}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-slate-600">{m.in_progress_jobs}</td>
                    <td className="py-2.5 px-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost" size="sm"
                            className="h-7 px-2 text-[11px] font-semibold"
                            data-testid={`prod-auto-mech-actions-${m.mechanic_id}`}
                          >
                            Report
                            <ChevronDown className="h-3 w-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => printMechanic(m, periodLabel, labourRate, workshopName)}
                            data-testid={`prod-auto-mech-print-${m.mechanic_id}`}
                          >
                            <Printer className="h-3.5 w-3.5 mr-2" />
                            Print PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => downloadCsv(
                              buildMechanicCsv(m, periodLabel, labourRate),
                              `${safeFile}_productivity_${periodFile}.csv`,
                            )}
                            data-testid={`prod-auto-mech-csv-${m.mechanic_id}`}
                          >
                            <FileDown className="h-3.5 w-3.5 mr-2" />
                            Export CSV
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
